// src/app/app.routes.ts
import { Routes } from '@angular/router';

import { AlreadyLoggedInGuard } from './core/guards/already-logged-in/already-logged-in.guard';
import { AuthGuard } from './core/guards/auth/auth.guard';
import { RoutePermissionGuard } from './core/guards/route-permission/route-permission.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./features/login/login.component').then((m) => m.LoginComponent),
    canActivate: [AlreadyLoggedInGuard],
  },

  {
    path: '',
    loadComponent: () =>
      import('./layouts/home-layout/home-layout.component').then(
        (m) => m.HomeLayoutComponent
      ),
    canActivate: [AuthGuard],
    children: [
      { path: '', redirectTo: '/dashboard', pathMatch: 'full' },

      // Dashboard: NO permission guard (always visible if logged in)
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboards/admin-dashboard/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent
          ),
      },

      // Clients
      {
        path: 'client',
        children: [
          {
            path: 'mobile-users',
            loadComponent: () =>
              import(
                './features/clients/cixor-mobile-users/cixor-mobile-user-management/cixor-mobile-user-management.component'
              ).then((m) => m.CixorMobileUserManagementComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'clients.mobileUsers', action: 'View' },
          },
          {
            path: 'employer',
            loadComponent: () =>
              import(
                './features/clients/employers/employers-and-requests/employers-and-requests.component'
              ).then((m) => m.EmployersAndRequestsComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'clients.employer', action: 'View' },
          },
          {
            path: 'company-employees',
            loadComponent: () =>
              import('./features/clients/company-employees/company-employees.component').then(
                (m) => m.CompanyEmployeesComponent
              ),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'clients.companyEmployees', action: 'View' },
          },
        ],
      },

      // Treasury
      {
        path: 'treasury',
        children: [
          {
            path: 'cumulative-income',
            loadComponent: () =>
              import(
                './features/treasury/revenue-command-centre/revenue-command-centre.component'
              ).then((m) => m.RevenueCommandCentreComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.cumulativeIncome', action: 'View' },
          },
          {
            path: 'wage-Withdrawals',
            loadComponent: () =>
              import('./features/treasury/wage-withdrawals/wage-withdrawals.component').then(
                (m) => m.WageWithdrawalsComponent
              ),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.wageWithdrawals', action: 'View' },
          },
          {
            path: 'wallet-withdrawals',
            loadComponent: () =>
              import(
                './features/treasury/wallet-withdrawals/wallet-withdrawals.component'
              ).then((m) => m.WalletWithdrawalsComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.walletWithdrawals', action: 'View' },
          },
          {
            path: 'gross-profit-dashboard',
            loadComponent: () =>
              import(
                './features/treasury/gross-profit-dashboard/gross-profit-dashboard.component'
              ).then((m) => m.GrossProfitDashboardComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.grossProfitDashboard', action: 'View' },
          },
          {
            path: 'settlement-and-Rollover',
            loadComponent: () =>
              import(
                './features/treasury/settlement-rollover/settlement-rollover.component'
              ).then((m) => m.SettlementRolloverComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.settlementRollover', action: 'View' },
          },
          {
            path: 'cost-of-fund-monitor',
            loadComponent: () =>
              import(
                './features/treasury/cost-of-funds-monitor/cost-of-funds-monitor.component'
              ).then((m) => m.CostOfFundsMonitorComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.costOfFundMonitor', action: 'View' },
          },
          {
            path: 'withdrawal-activity-monitor',
            loadComponent: () =>
              import(
                './features/treasury/withdrawal-activity-monitor/withdrawal-activity-monitor.component'
              ).then((m) => m.WithdrawalActivityMonitorComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.withdrawalActivityMonitor', action: 'View' },
          },
          {
            path: 'liquidity-and-fund-utilization',
            loadComponent: () =>
              import(
                './features/treasury/liquidity-and-fund-utilization/liquidity-and-fund-utilization.component'
              ).then((m) => m.LiquidityAndFundUtilizationComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.liquidityAndFundUtilization', action: 'View' },
          }
        ],
      },

      // Cixor Payment Management
      {
        path: 'manageCixorPayments',
        children: [
          {
            path: 'cixor-payments',
            loadComponent: () =>
              import('./features/manage-payment/cixor-payments/cixor-payment.component').then(
                (m) => m.CreditLineComponent
              ),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'manageCixorPayments.cixorPayments', action: 'View' },
          },
          {
            path: 'payment-history',
            loadComponent: () =>
              import('./features/manage-payment/payment-history/payment-history.component').then(
                (m) => m.PaymentHistoryComponent
              ),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'manageCixorPayments.paymentHistory', action: 'View' },
          },
          {
            path: 'payroll-payments',
            loadComponent: () =>
              import(
                './features/manage-payment/payroll-payments/payroll-payments.component'
              ).then((m) => m.PayrollPaymentsComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'manageCixorPayments.payrollPayments', action: 'View' },
          },
          {
            path: 'late-payment-fees',
            loadComponent: () =>
              import(
                './features/manage-payment/late-fee-payments/late-fee-payments.component'
              ).then((m) => m.LateeFeePaymentsComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'manageCixorPayments.latePaymentFees', action: 'View' },
          },
        ],
      },

      // Fund Management
      {
        path: 'fundManagement',
        children: [
          {
            path: 'contributor-management',
            loadComponent: () =>
              import(
                './features/fundManagement/contributor-management/contributor-management.component'
              ).then((m) => m.ContributorManagementComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'fundManagement.contributorManagement', action: 'View' },
          },
          {
            path: 'summary-management',
            loadComponent: () =>
              import(
                './features/fundManagement/summary-management/summary-management.component'
              ).then((m) => m.SummaryManagementComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'fundManagement.summaryManagement', action: 'View' },
          },
        ],
      },

      // Credit & Risk
      {
        path: 'credit-and-risk',
        children: [
          {
            path: 'rollover-management',
            loadComponent: () =>
              import(
                './features/credit-and-risk/rollover-management/rollover-management.component'
              ).then((m) => m.RolloverManagementComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'creditRisk.manageRollovers', action: 'View' },
          },
          {
            path: 'credit-management',
            loadComponent: () =>
              import(
                './features/credit-and-risk/credit-management/credit-management.component'
              ).then((m) => m.CreditManagementComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'creditRisk.manageCredit', action: 'View' },
          },
        ],
      },

      // Ops Risk & Compliance
      {
        path: 'ops-risk-and-compliance',
        loadComponent: () =>
          import(
            './features/ops-risk-and-compliance/ops-risk-and-compliance.component'
          ).then((m) => m.OpsRiskAndComplianceComponent),
        canActivate: [AuthGuard, RoutePermissionGuard],
        data: { moduleKey: 'opsRiskCompliance', action: 'View' },
      },

      // Configuration
      {
        path: 'configuration',
        children: [
          {
            path: 'late-fee-configuration',
            loadComponent: () =>
              import(
                './features/configuration/late-fee-configuration/late-fee-configuration.component'
              ).then((m) => m.LateFeeConfigurationComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'configuration.lateFeeConfiguration', action: 'View' },
          },
          {
            path: 'industry-configuration',
            loadComponent: () =>
              import(
                './features/configuration/industry-configuration/industry-configuration.component'
              ).then((m) => m.IndustryConfigurationComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'configuration.industryConfiguration', action: 'View' },
          },
        ],
      },

      // User Management
      {
        path: 'user-management',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './features/portal-user-management/users-and-requests/users-and-requests.component'
              ).then((m) => m.UsersAndRequestsComponent),
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'userManagement', action: 'View' },
          },
        ],
      },
    ],
  },

  // Fallback
  { path: '**', redirectTo: '/dashboard' },
];